import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Button,
  Input,
  Popover,
  PopoverHeader,
  PopoverBody,
  FormGroup,
  Label,
  Row,
  Col,
  Container,
} from "reactstrap";
import {
  getProspectEngagementDashboard,
  createBroadcast,
  importLeads,
} from "../service";
import { Notification } from "../../common/style";

const ProspectEngagement = () => {
  const [prospects, setProspects] = useState([]);
  const [selected, setSelected] = useState([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverTarget, setPopoverTarget] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [search, setSearch] = useState("");
  const [templateFilter, setTemplateFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [activityFilter, setActivityFilter] = useState("All Orgs");
  const [metFilter, setMetFilter] = useState("All");
  const [demoFilter, setDemoFilter] = useState("All");
  const [selectedRows, setSelectedRows] = useState([]);
  const [sendMode, setSendMode] = useState("now");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleHour, setScheduleHour] = useState("");
  const [scheduleMinute, setScheduleMinute] = useState("");
  const [ampm, setAmpm] = useState("AM");
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const [notificationMessage, setNotificationMessage] = useState({});
  const fileInputRef = useRef(null);
  const templates = [
    "new_profiled_bot_signup_16_06_2025",
    "schedule_meeting_try_first_order_fix_time",
    "multiple_spec_price_check_11_06_2025",
    "new_profiled_signup_03_06_2025",
    "signup_met_demo_price_comparison_30_05_2025",
  ];

  useEffect(() => {
    getProspectEngagementDashboard().then((res) => {
      setProspects(res);
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
  
    setSearch(params.get("search") || "");
    setTemplateFilter(params.get("template") || "All");
    setStatusFilter(params.get("status") || "All");
    setActivityFilter(params.get("activity") || "All Orgs");
    setMetFilter(params.get("met") || "All");
    setDemoFilter(params.get("demo") || "All");
  
    getProspectEngagementDashboard().then((res) => {
      setProspects(res);
    });
  }, []);

  useEffect(() => updateQueryParam("search", search), [search]);
  useEffect(() => updateQueryParam("template", templateFilter), [templateFilter]);
  useEffect(() => updateQueryParam("status", statusFilter), [statusFilter]);
  useEffect(() => updateQueryParam("activity", activityFilter), [activityFilter]);
  useEffect(() => updateQueryParam("met", metFilter), [metFilter]);
  useEffect(() => updateQueryParam("demo", demoFilter), [demoFilter]);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const updateQueryParam = (key, value) => {
    const params = new URLSearchParams(window.location.search);
    if (value === "All" || value === "" || value === "All Orgs") {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  };


  // Get unique template names and delivery statuses from data
  const uniqueTemplates = [
    ...new Set(prospects.map((p) => p.last_message?.template).filter(Boolean)),
  ];
  const uniqueStatuses = [
    ...new Set(prospects.map((p) => p.last_message?.delivery_status).filter(Boolean)),
  ];

  // Filtered prospects based on search and filters
  const filteredProspects = prospects.filter((p) => {
    const matchesSearch = p.customer?.toLowerCase().includes(search.toLowerCase());
    const matchesTemplate =
      templateFilter === "All" || p.last_message?.template === templateFilter;
    const matchesStatus =
      statusFilter === "All" || p.last_message?.delivery_status === statusFilter;
    let matchesActivity = true;
    if (activityFilter === "Leads") {
      matchesActivity = p.status === "lead";
    } else if (activityFilter === "Active Prospects") {
      matchesActivity = p.status === "prospect" && p.active;
    } else if (activityFilter === "Inactive Prospects") {
      matchesActivity = p.status === "prospect" && !p.active;
    }
    const matchesMet =
      metFilter === "All" || (metFilter === "Met" ? p.met : !p.met);
    const matchesDemo =
      demoFilter === "All" || (demoFilter === "Demo" ? p.demo : !p.demo);
    return matchesSearch && matchesTemplate && matchesStatus && matchesActivity && matchesMet && matchesDemo;
  });

  const formatShortDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      month: "short",
      day: "numeric",
    });
  };

  const formatShortDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const toggleSelection = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const allFilteredIds = filteredProspects.map((p) => p.id);
  const isAllSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => selectedRows.includes(id));
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows((prev) => prev.filter((id) => !allFilteredIds.includes(id)));
    } else {
      setSelectedRows((prev) => Array.from(new Set([...prev, ...allFilteredIds])));
    }
  };

  const openPopover = (e) => {
    setPopoverTarget(e.target);
    setPopoverOpen(true);
  };

  const handleSendMessage = () => {
    const payload = {
      templateId: selectedTemplate,
      orgIds: selectedRows,
    };

    if (
      sendMode === "scheduled" &&
      scheduleDate &&
      scheduleHour &&
      scheduleMinute &&
      ampm
    ) {
      let hour = parseInt(scheduleHour, 10);
      const minute = parseInt(scheduleMinute, 10);
      if (ampm === "PM" && hour < 12) hour += 12;
      if (ampm === "AM" && hour === 12) hour = 0;
      const dateTimeStr = `${scheduleDate}T${hour
        .toString()
        .padStart(2, "0")}:${minute.toString().padStart(2, "0")}:00`;
      const local = new Date(dateTimeStr);
      const utcISOString = new Date(
        local.getTime() - local.getTimezoneOffset() * 60000
      ).toISOString();
      payload.schedule_time = utcISOString;
    }

    createBroadcast(payload)
      .then(() => {
        setPopoverOpen(false);
        setSelectedRows([]);
        setSelectedTemplate("");
        setSendMode("now");
        setScheduleDate("");
        setScheduleHour("");
        setScheduleMinute("");
        setAmpm("AM");
      })
      .catch(() => {
        alert("Error");
      });
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileUpload = async (e) => {

    const file = e.target.files?.[0];

    if (!file) {
      alert("No file selected.");
      return;
    }

    if (!file.name.endsWith(".csv")) {
      alert("Please upload a valid CSV file.");
      return;
    }

    try {
      const result = await importLeads(file);

      const refreshed = await getProspectEngagementDashboard();
      setProspects(refreshed);

      setIsNotificationVisible(true);
      setNotificationMessage({
        text: result.message || "CSV imported successful.",
        type: "success",
      });
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error?.message || error?.error || "Import failed. Try again.";

      setIsNotificationVisible(true);
      setNotificationMessage({
        text: message,
        type: "danger",
      });
    }
  };

  return (
    <Container fluid className="py-4 px-3">
      {notificationMessage.text && (
        <Notification
          color={notificationMessage.type}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notificationMessage.text}
        </Notification>
      )}

      <div className="d-flex justify-content-between align-items-center">
        {" "}
        <h5 className="mb-4">Prospect Engagement</h5>
        <div className="mb-3 text-end pe-2">
          <Button
            color="outline-secondary"
            className="me-2 mb-0"
            onClick={handleFileButtonClick}
          >
            Import
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
        </div>
      </div>

      {/* Filters and Search */}
      <Row className="g-3 mb-4 align-items-end flex-nowrap overflow-auto">
        <Col
          xs="12"
          sm="6"
          md="3"
          lg="2"
          className="flex-shrink-0"
          style={{ minWidth: 150, maxWidth: 200 }}
        >
          <Input
            type="search"
            placeholder="Search customer name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-control"
            style={{ fontSize: "1rem" }}
          />
        </Col>
        <Col xs="12" sm="6" md="3" lg="2" className="flex-shrink-0" style={{ minWidth: 150, maxWidth: 200 }}>
          <Input
            type="select"
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            className="form-select"
            style={{ fontSize: "1rem" }}
          >
            <option value="All Orgs">All Orgs</option>
            <option value="Leads">Leads</option>
            <option value="Active Prospects">Active Prospects</option>
            <option value="Inactive Prospects">Inactive Prospects</option>
          </Input>
        </Col>
        <Col xs="12" sm="6" md="3" lg="2" className="flex-shrink-0" style={{ minWidth: 150, maxWidth: 200 }}>
          <Input
            type="select"
            value={metFilter}
            onChange={(e) => setMetFilter(e.target.value)}
            className="form-select"
            style={{ fontSize: "1rem" }}
          >
            <option value="All">All Met</option>
            <option value="Met">Met</option>
            <option value="Not Met">Not Met</option>
          </Input>
        </Col>
        <Col xs="12" sm="6" md="3" lg="2" className="flex-shrink-0" style={{ minWidth: 150, maxWidth: 200 }}>
          <Input
            type="select"
            value={demoFilter}
            onChange={(e) => setDemoFilter(e.target.value)}
            className="form-select"
            style={{ fontSize: "1rem" }}
          >
            <option value="All">All Demo</option>
            <option value="Demo">Demo</option>
            <option value="No Demo">No Demo</option>
          </Input>
        </Col>
        <Col xs="12" sm="6" md="3" lg="2" className="flex-shrink-0" style={{ minWidth: 150, maxWidth: 200 }}>
          <Input
            type="select"
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
            className="form-select"
            style={{ fontSize: "1rem" }}
          >
            <option value="All">All Templates</option>
            {uniqueTemplates.map((tpl) => (
              <option key={tpl} value={tpl}>{tpl}</option>
            ))}
          </Input>
        </Col>
        <Col xs="12" sm="6" md="3" lg="2" className="flex-shrink-0" style={{ minWidth: 150, maxWidth: 200 }}>
          <Input
            type="select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-select"
            style={{ fontSize: "1rem" }}
          >
            <option value="All">All Message Status</option>
            {uniqueStatuses.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </Input>
        </Col>
      </Row>

      <div className="mb-3 text-end pe-2">
        <Button
          id="sendMessageBtn"
          color="primary"
          onClick={openPopover}
          disabled={selectedRows.length === 0}
        >
          Send Message ({selectedRows.length})
        </Button>

        <Popover
          placement="bottom"
          isOpen={popoverOpen}
          target={popoverTarget}
          toggle={() => setPopoverOpen(!popoverOpen)}
          className="border rounded shadow"
          modifiers={[
            { name: "offset", options: { offset: [0, 10] } },
            { name: "preventOverflow", options: { padding: 16 } },
          ]}
        >
          <PopoverHeader className="d-flex justify-content-between align-items-center">
            <span>Send Message</span>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setPopoverOpen(false)}
            />
          </PopoverHeader>

          <PopoverBody>
            <div className="p-2 pb-0">
              <FormGroup>
                <Label for="templateSelect" className="fw-bold">
                  Template
                </Label>
                <Input
                  type="select"
                  id="templateSelect"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {templates.map((tpl) => (
                    <option key={tpl} value={tpl}>
                      {tpl}
                    </option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup tag="fieldset" className="mt-3">
                <legend className="fw-bold small">When to Send?</legend>
                <FormGroup check>
                  <Input
                    type="radio"
                    name="sendMode"
                    value="now"
                    checked={sendMode === "now"}
                    onChange={() => setSendMode("now")}
                  />
                  <Label check className="ms-2">
                    Now
                  </Label>
                </FormGroup>
                <FormGroup check className="mt-2">
                  <Input
                    type="radio"
                    name="sendMode"
                    value="scheduled"
                    checked={sendMode === "scheduled"}
                    onChange={() => setSendMode("scheduled")}
                  />
                  <Label check className="ms-2">
                    Schedule
                  </Label>
                </FormGroup>
              </FormGroup>

              {sendMode === "scheduled" && (
                <>
                  <FormGroup className="mt-2">
                    <Label className="fw-bold">Schedule Date</Label>
                    <Input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup className="mt-2">
                    <Label className="fw-bold">Schedule Time</Label>
                    <Row className="g-2">
                      <Col xs={4}>
                        <Input
                          type="select"
                          value={scheduleHour}
                          onChange={(e) => setScheduleHour(e.target.value)}
                        >
                          <option value="">Hour</option>
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          ))}
                        </Input>
                      </Col>

                      <Col xs={4}>
                        <Input
                          type="select"
                          value={scheduleMinute}
                          onChange={(e) => setScheduleMinute(e.target.value)}
                        >
                          <option value="">Minute</option>
                          {Array.from({ length: 60 }, (_, i) => (
                            <option
                              key={i}
                              value={i.toString().padStart(2, "0")}
                            >
                              {i.toString().padStart(2, "0")}
                            </option>
                          ))}
                        </Input>
                      </Col>

                      <Col xs={4}>
                        <Input
                          type="select"
                          value={ampm}
                          onChange={(e) => setAmpm(e.target.value)}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </Input>
                      </Col>
                    </Row>
                  </FormGroup>
                </>
              )}

            <div className="d-flex justify-content-end gap-2 p-2 bg-EEEEEE">
                <Button color="secondary" onClick={() => setPopoverOpen(false)}>
                  Cancel
                </Button>
                <Button
                  color="success"
                  onClick={handleSendMessage}
                  disabled={
                    !selectedTemplate ||
                    (sendMode === "scheduled" &&
                      (!scheduleDate || !scheduleHour || !scheduleMinute))
                  }
                >
                  Send
                </Button>
              </div>
            </div>
          </PopoverBody>
        </Popover>
      </div>

      <Table bordered responsive hover className="align-middle w-100 small" style={{ minWidth: 900, fontSize: '12px' }}>
        <thead className="table-secondary">
          <tr>
            <th className="text-center" style={{ width: 48 }}>
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleSelectAll}
                className="form-check-input"
                style={{ width: 24, height: 24 }}
              />
            </th>
            <th>Customer</th>
            <th>Users</th>
            <th>Status</th>
            <th>City</th>
            <th>Last template</th>
          </tr>
        </thead>
        <tbody>
          {filteredProspects.map((p, idx) => {
            const isSelected = selectedRows.includes(p.id);
            return (
              <tr
                key={p.id}
                className={
                  isSelected
                    ? "selected-row"
                    : idx % 2 === 0
                    ? "table-light"
                    : ""
                }
                style={{
                  cursor: "pointer",
                  background: isSelected ? '#d0e7ff' : idx % 2 === 0 ? '#f8f9fa' : undefined,
                  borderLeft: isSelected ? '6px solid #1976d2' : undefined,
                  transition: 'background 0.2s, border-left 0.2s',
                }}
                onClick={() => toggleRow(p.id)}
              >
                <td className="text-center align-middle" style={{ width: 48 }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleRow(p.id)}
                    className="form-check-input"
                    style={{ width: 24, height: 24 }}
                    onClick={e => e.stopPropagation()}
                  />
                </td>
                <td className="py-3 px-4">
                  {p.customer} #({p.id})
                </td>
                <td className="py-3 px-4">{p.users_info}</td>
                <td className="py-3 px-4">{p.status}</td>
                <td className="py-3 px-4">{p.city}</td>
                <td className="py-3 px-4">
                  {p.last_message ? (
                    <>
                      <div>{p.last_message.template}</div>
                      <div className="text-muted small">
                        {formatShortDateTime(p.last_message.sent_at)} ({p.last_message.delivery_status})
                      </div>
                    </>
                  ) : (
                    <span className="text-muted">No messages</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Container>
  );
};

export default ProspectEngagement;
